import React from 'react';
import moment from 'moment';

import { DataTable, ColumnConfig } from '../../components/Table';
import { CardTitle } from '../../components/Card';
import Pagination from '../../components/Pagination/Pagination';
import PatchDetailsModal from '../../components/Modal/PatchDetailsModal';
import { PolicyMode } from '../../components/services/softwareComplianceService';

import { Asset, BannerCard, GroupGroupedRow, OverrideMode, SoftwareSummary, VersionGroupedRow } from './infoTableTypes';
import { usePermissions } from '../../hooks/usePermissions';

// ================= PROPS =================
export interface SoftwareInfoViewProps {
  softwareInfo: SoftwareSummary;
  bannerCards: BannerCard[];
  bannerColumns: number;
  hasSoftwarePolicy: boolean;
  softwarePolicyMode: PolicyMode | null;
  tableData: Asset[];
  columns: ColumnConfig[];
  versionColumns: ColumnConfig[];
  groupColumns: ColumnConfig[];
  versionViewData: VersionGroupedRow[];
  groupViewData: GroupGroupedRow[];
  loading: boolean;
  showActiveOnly: boolean;
  showPatchModal: boolean;
  selectedRow: any;
  currentPage: number;
  totalPages: number;
  selectedItemIds: string[];
  viewMode: 'device' | 'version' | 'group';
  viewOptions: readonly { val: 'device' | 'version' | 'group'; label: string }[];
  activeViewIndex: number;
  selectedVersionKeys: Set<string>;
  selectedGroupKeys: Set<string>;
  selectedPolicyAction: OverrideMode | '';
  submittingVersionPolicy: boolean;
  onActiveFilter: () => void;
  onOpenRemediateModal: () => void;
  onPageChange: (page: number) => void;
  onCloseModal: () => void;
  onDownloadCSV: () => void;
  onViewModeChange: (mode: 'device' | 'version' | 'group') => void;
  onSelectedPolicyActionChange: (action: OverrideMode | '') => void;
  onSubmitVersionPolicy: () => void;
}

// ================= COMPONENT =================
const SoftwareInfoView: React.FC<SoftwareInfoViewProps> = ({
  softwareInfo,
  bannerCards,
  bannerColumns,
  hasSoftwarePolicy,
  softwarePolicyMode,
  tableData,
  columns,
  versionColumns,
  groupColumns,
  versionViewData,
  groupViewData,
  loading,
  showActiveOnly,
  showPatchModal,
  selectedRow,
  currentPage,
  totalPages,
  selectedItemIds,
  viewMode,
  viewOptions,
  activeViewIndex,
  selectedVersionKeys,
  selectedGroupKeys,
  selectedPolicyAction,
  submittingVersionPolicy,
  onActiveFilter,
  onOpenRemediateModal,
  onPageChange,
  onCloseModal,
  onDownloadCSV,
  onViewModeChange,
  onSelectedPolicyActionChange,
  onSubmitVersionPolicy,
}) => {
  const isVersionOrGroupView = viewMode === 'version' || viewMode === 'group';
  const selectedCount = viewMode === 'version' ? selectedVersionKeys.size : selectedGroupKeys.size;
  const { canEdit } = usePermissions();
  const canRemediate = canEdit('remediationPlan');

  return (
    <div className="p-4 bg-black rounded text-white">
      {/* HEADER */}
      <div className="mb-6">
        {softwareInfo ? (
          <div className="flex items-center gap-3 mb-4">
            <h2 className="text-3xl font-bold">{softwareInfo.softwareAssetName}</h2>
            {hasSoftwarePolicy && (
              <span
                className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold tracking-[0.08em] ${
                  softwarePolicyMode === PolicyMode.Allowlist
                    ? 'border-[#00C16A] bg-[#06281A] text-[#38FFB3]'
                    : softwarePolicyMode === PolicyMode.Partial
                      ? 'border-[#D6A300] bg-[#2C2206] text-[#FFD66B]'
                      : 'border-[#D12B2B] bg-[#2A0B0B] text-[#FF8A8A]'
                }`}
              >
                {softwarePolicyMode === PolicyMode.Allowlist
                  ? 'ALLOWED'
                  : softwarePolicyMode === PolicyMode.Partial
                    ? 'PARTIAL'
                    : 'BLOCKED'}
              </span>
            )}
          </div>
        ) : (
          <div className="h-10 w-64 bg-gray-700 animate-pulse rounded mb-4"></div>
        )}

        {/* CARDS */}
        <div className="grid gap-4 w-full" style={{ gridTemplateColumns: `repeat(${bannerColumns}, minmax(0, 1fr))` }}>
          {softwareInfo
            ? bannerCards.map((card, i) => (
                <div
                  key={i}
                  style={{ backgroundColor: i % 2 === 0 ? '#2E2D2D' : '#1D1D1D' }}
                  className="h-[120px] p-4 rounded-lg shadow flex flex-col overflow-hidden"
                >
                  <div className="mb-2">
                    <CardTitle title={card.label} />
                  </div>
                  <div className="flex-1 flex items-center">
                    <span className="text-3xl font-bold ml-[28px] truncate w-[90%]">{card.value}</span>
                  </div>
                  {card.date && (
                    <div className="mt-auto">
                      <span className="text-xs text-gray-400 ml-[28px] opacity-70">
                        {card.date && !card.date.startsWith('0001-01-01') ? moment(card.date).format('DD MMM YYYY') : '-'}
                      </span>
                    </div>
                  )}
                </div>
              ))
            : Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="h-[120px] p-4 rounded-lg shadow flex flex-col animate-pulse"
                  style={{ backgroundColor: i % 2 === 0 ? '#2E2D2D' : '#1D1D1D' }}
                >
                  <div className="h-6 w-24 bg-gray-600 rounded mb-2"></div>
                  <div className="flex-1 flex items-center">
                    <div className="h-10 w-32 bg-gray-500 rounded ml-[28px]"></div>
                  </div>
                  <div className="mt-auto">
                    <div className="h-3 w-20 bg-gray-500 rounded ml-[28px] opacity-70"></div>
                  </div>
                </div>
              ))}
        </div>
      </div>

      {/* ACTIONS */}
      <div className="flex gap-2 justify-end mb-5">
        {/* VIEW MODE TOGGLE */}
        <div className="relative flex items-center bg-[#1C1C1D] border border-[#333] rounded-[10px] p-1 mr-auto">
          <div
            className="absolute rounded-[7px] transition-all duration-300 ease-in-out"
            style={{
              top: '4px',
              bottom: '4px',
              left: '4px',
              width: `calc((100% - 8px) / ${viewOptions.length})`,
              transform: `translateX(${activeViewIndex * 100}%)`,
              background: '#2a2a2b',
            }}
          />
          {viewOptions.map(({ val, label }) => (
            <button
              key={val}
              onClick={() => onViewModeChange(val)}
              className={`relative z-10 flex items-center justify-center gap-1.5 px-4 py-1.5 text-[13px] font-medium whitespace-nowrap transition-colors duration-200 ${
                viewMode === val ? 'text-white' : 'text-[#888] hover:text-[#ccc]'
              }`}
              style={{ width: `${100 / viewOptions.length}%` }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* VERSION POLICY CONTROLS */}
        {isVersionOrGroupView && (
          <div className="flex items-center gap-3">
            <div className="relative">
              <select
                disabled={selectedCount === 0 || submittingVersionPolicy}
                value={selectedPolicyAction}
                onChange={(e) => onSelectedPolicyActionChange(e.target.value as OverrideMode | '')}
                className="appearance-none min-w-[180px] px-4 py-2 pr-10 rounded-lg bg-[#1A1A1A] border border-[#3A3A3A] text-white font-medium outline-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:border-[#5A5A5A] transition-all"
              >
                <option value="" disabled>
                  Select Policy
                </option>
                <option value="Blacklist">Blacklist</option>
                <option value="Allowlist">Allowlist</option>
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-400">▼</div>
            </div>

            <button
              disabled={!selectedPolicyAction || selectedCount === 0 || submittingVersionPolicy}
              onClick={onSubmitVersionPolicy}
              className={`bg-red-600 px-4 py-2 rounded text-sm ${
                !selectedPolicyAction || selectedCount === 0 || submittingVersionPolicy
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-red-700'
              } transition-colors`}
            >
              {submittingVersionPolicy ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        )}

        {/* REMEDIATE */}
        {canRemediate &&
          softwareInfo.packageIdentifier &&
          softwareInfo.latestPackageVersion !== softwareInfo.softwareAssetVersion &&
          !isVersionOrGroupView && (
            <button
              className={`px-4 py-2 rounded text-sm ${
                selectedItemIds.length === 0 ? 'bg-gray-600 opacity-50' : 'bg-red-600 hover:bg-red-700'
              }`}
              onClick={onOpenRemediateModal}
              disabled={selectedItemIds.length === 0}
            >
              Remediate
            </button>
          )}

        <button type="button" className="bg-green-600 px-4 py-2 rounded" onClick={onDownloadCSV} disabled={loading}>
          Download CSV
        </button>

        {!isVersionOrGroupView && (
          <button className="bg-green-600 px-4 py-2 rounded" onClick={onActiveFilter}>
            {showActiveOnly ? 'Show All' : 'Active'}
          </button>
        )}
      </div>

      {/* TABLE */}
      <DataTable
        columns={viewMode === 'version' ? versionColumns : viewMode === 'group' ? groupColumns : columns}
        data={viewMode === 'version' ? versionViewData : viewMode === 'group' ? groupViewData : tableData}
        getRowId={(item: any) => item.tenantAgentID || item.softwareAssetVersion || item.tenantGroupId}
      />

      {/* PAGINATION */}
      <Pagination currentPage={currentPage} totalPages={totalPages} loading={loading} onPageChange={onPageChange} />

      {/* MODAL */}
      {showPatchModal && (
        <PatchDetailsModal
          show={showPatchModal}
          selectedRow={selectedRow}
          heartbeatResponse={null}
          isLoading={false}
          onClose={onCloseModal}
        />
      )}
    </div>
  );
};

export default SoftwareInfoView;
